/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2018, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

angular.module('bulbApp.contentEditorModule')
  .service('BulbContentEditorService', ['CONTENT_EDITOR', BulbContentEditorService]);

/**
 * Handles miscellaneous functions/utilities that relate directly to the ContentEditor used on bulb.
 */
function BulbContentEditorService(CONTENT_EDITOR) {
  const that = this;

  this.contentEditorSettings = CONTENT_EDITOR;

  // All colors from .css are returned in rgb format, but we use HEX format, this function perfectly converts from
  // rgb to HEX and is used for font colors.
  this.convertRGBtoHEX = function (rgbValue) {
    if (rgbValue.search('rgb') === -1) {
      return null;
    }
    rgbValue = rgbValue.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    function hex(x) {
      return (`0${parseInt(x).toString(16)}`).slice(-2);
    }
    return `#${hex(rgbValue[1])}${hex(rgbValue[2])}${hex(rgbValue[3])}`;
  };

  this.isAllowedTag = function (element) {
    // Since we're just checking simple nodeNames, let's just accept normal elements.
    if (element instanceof jQuery) {
      element = element[0];
    }

    // Check if the allowed tags on the bulb editor includes the tag we are testing.
    if (CONTENT_EDITOR.ALLOWED_TAGS.includes(element.nodeName.toLowerCase())) {
      return true;
    }

    return false;
  };

  /**
   * This function returns an object with the keys of [accepted] and [formattedFont], since the change can/should occur
   * in this service instead of wherever the function is being used. For some reason, browsers wrap " tags around the
   * font-family if it's more than one word. We need to remove those for proper checking in most places.
   * @param element The element that needs to be checked.
   */
  this.hasBulbFont = function (element) {
    // We can use jQuery to just make things easier.
    if (!(element instanceof jQuery)) {
      element = $(element);
    }

    // Grab the font family, if there isn't one, we'll return false.
    const fontFamily = element.css('font-family');

    // If the font exists, it is not the default font, and it is not "initial", then we return the font.
    if (fontFamily && fontFamily !== CONTENT_EDITOR.DEFAULT_FONT.FONT && fontFamily !== 'initial') {
      // We found a font, and it's not an initial font, nor the default value for bulb. Now we have to check
      // if the font is actually allowed. If so, we can return true.
      if (CONTENT_EDITOR.FONTS.find((font) => font.FONT === fontFamily)) {
        return {
          accepted: true,
          formattedFont: fontFamily,
        };
      }
    }

    return {
      accepted: false,
      formattedFont: null,
    };
  };

  /**
   * This is a function that returns an object that says whether or not the font-color is allowed, as well as the
   * converted font color from RGB to HEX. Since all color variables come in as RGB values, we need access to that
   * color from wherever this function is used from.
   * @param element The element that needs to be checked.
   */
  this.hasBulbColor = function (element) {
    // We can just use jQuery to make it easier.
    if (!(element instanceof jQuery)) {
      element = $(element);
    }

    // Grab the color, if there isn't one, we'll return false.
    const fontColor = that.convertRGBtoHEX(element.css('color'));

    // If the font exists, it is not the default font, and it is not "initial", then we return the font.
    if (fontColor && fontColor !== CONTENT_EDITOR.DEFAULT_COLOR && fontColor !== 'initial') {
      // So there's a font, and we found it. We now just need to make sure it's an allowed font.
      if (CONTENT_EDITOR.COLORS.includes(fontColor)) {
        return {
          accepted: true,
          colorInHEX: fontColor,
        };
      }
    }

    return {
      accepted: false,
      colorInHEX: null,
    };
  };

  /**
   * Function that modifies the span if it has styling applied to it.
   *
   * @param currentElement The current element that we are going to modify.
   * @param newStyle The styling applied to the currentSpan in the form of a String.
   */
  this.applySpecificStyling = function (currentElement, newStyle) {
    if (newStyle.includes('bold')) {
      currentElement.wrap('<b></b>');
    }
    if (newStyle.includes('italic')) {
      currentElement.wrap('<i></i>');
    }
    if (newStyle.includes('underline')) {
      currentElement.wrap('<u></u>');
    }
  };

  /**
   * Function that checks for bold, italic, and underline styles.
   *
   * @param elementStyling {String} The styles extracted from the current element.
   */
  this.hasBoldItalicUnderlineStyling = function (elementStyling) {
    const stylingArray = elementStyling.split(';').filter((style) => style !== '');

    // Remove the potential whitespace from the entries in the styling array:
    // This is needed because if there are some whitespaces that exists within the entries of the styling array,
    // The conditional checks below would fail.
    stylingArray.forEach(function (part, index) {
      this[index] = this[index].trim();
    }, stylingArray); // use stylingArray as 'this'

    return stylingArray.includes('text-decoration-line: underline')
         || stylingArray.includes('text-decoration: underline') // needed for Safari
         || stylingArray.includes('font-style: italic')
         || stylingArray.includes('font-weight: bold');
  };
}
